import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { createMask } from '@ngneat/input-mask';
import { from } from 'rxjs';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ToastService } from 'src/app/services/toast.service';
import { companyBlocklistedTaxIdsService } from 'src/app/shared/vive-api/company-blocklisted-tax-ids.service';
import {
  CompanyBlocklistedTaxIdDto,
  CompanyBlocklistedTaxIdsCreateRequestDtoTaxIdTypeEnum
} from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

@Component({
  selector: 'app-blocked-tax-ids-modal',
  templateUrl: './blocked-tax-ids-modal.component.html',
  styleUrl: './blocked-tax-ids-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BlockedTaxIdsModalComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _modalService = inject(NgbModal);

  constructor(
    private activeModal: NgbActiveModal,
    private ToastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input() CompanyId!: number;
  @Input() blockedTaxIds: CompanyBlocklistedTaxIdDto[] = [];
  @Input() country: string = 'US';

  einMask = createMask('99-9999999');
  ssnMask = createMask('999-99-9999');
  gstHstMask = createMask('99-9999999');

  addForm = new FormGroup({
    taxId: new FormControl('', [Validators.required, Validators.minLength(9)]),
    taxIdType:
      new FormControl<CompanyBlocklistedTaxIdsCreateRequestDtoTaxIdTypeEnum>(
        'EIN',
        [Validators.required]
      ),
    note: new FormControl('')
  });

  ngOnInit() {
    if (this.country === 'CA') {
      this.addForm.patchValue({ taxIdType: 'GST_HST' });
    }
  }

  get isCanada(): boolean {
    return this.country === 'CA';
  }

  get currentMask() {
    const taxIdType = this.addForm.get('taxIdType')?.value;
    if (taxIdType === 'SSN') return this.ssnMask;
    if (taxIdType === 'GST_HST') return this.gstHstMask;
    return this.einMask;
  }

  addBlockedTaxId() {
    if (!this.addForm.valid) {
      this.ToastService.addErrorToast('Error', 'Please enter a valid Tax ID');
      return;
    }

    const { taxId, taxIdType, note } = this.addForm.value;

    from(
      companyBlocklistedTaxIdsService.create({
        pathParams: { companyId: this.CompanyId },
        data: {
          taxId: taxId!,
          taxIdType: taxIdType!,
          Note: note ? { note } : undefined
        },
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
      })
    )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (newItem) => {
          this.ToastService.addSuccessToast(
            'Success',
            'Tax ID added to blocklist'
          );
          this.addForm.reset({ taxIdType: this.isCanada ? 'GST_HST' : 'EIN' });
          this.blockedTaxIds = [newItem, ...this.blockedTaxIds];
          this.cdr.detectChanges();
        }
      });
  }

  deleteBlockedTaxId(id: number) {
    const modalRef = this._modalService.open(ConfirmModalComponent, {
      centered: true,
      size: 'md'
    });
    Object.assign(modalRef.componentInstance, {
      displayText:
        'Are you sure you want to remove this tax ID from the blocklist?'
    });

    modalRef.closed.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (result: boolean) => {
        if (result) {
          from(
            companyBlocklistedTaxIdsService.deleteById({
              pathParams: { companyId: this.CompanyId, id },
              options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
            })
          )
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Tax ID removed from blocklist'
                );
                this.blockedTaxIds = this.blockedTaxIds.filter(
                  (item) => item.id !== id
                );
                this.cdr.detectChanges();
              },
              error: () => {
                this.ToastService.addErrorToast(
                  'Error',
                  'Failed to remove tax ID'
                );
              }
            });
        }
      }
    });
  }

  formatTaxId(taxId: string, type: string): string {
    if ((type === 'EIN' || type === 'GST_HST') && taxId.length === 9) {
      return `${taxId.slice(0, 2)}-${taxId.slice(2)}`;
    }
    if (type === 'SSN' && taxId.length === 9) {
      return `${taxId.slice(0, 3)}-${taxId.slice(3, 5)}-${taxId.slice(5)}`;
    }
    return taxId;
  }

  getTypeLabel(type: string): string {
    if (type === 'GST_HST') return 'GST/HST';
    return type;
  }

  getNoteText(item: CompanyBlocklistedTaxIdDto): string {
    return item.Note?.note || '-';
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  close() {
    this.activeModal.close(this.blockedTaxIds.length);
  }
}
